import CandidatePage from "../../../../components/candidate/CandidatePage";
import { CxenseParseTag, SeoTag } from "../../../../components/seo/SeoTag";
import {
  getPresidentList,
  getCandidateProfile,
  getCandidateParticulars,
  getVicePresidentList,
  getSenatorList,
  getLocalCandidatesList,
} from "../../../api/ph-api";

const basePath = process.env.SITE_URL;

export const getStaticPaths = async () => {
  const presidents = await getPresidentList();
  const vicePresidents = await getVicePresidentList();
  const senators = await getSenatorList();

  const candidates = presidents.results.concat(
    vicePresidents.results,
    senators.results
  );

  const paths = candidates.map((candidate) => ({
    params: { slug: candidate.wp_slug },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps = async (context) => {
  let { slug } = context.params;
  if ( ["undefined", "null"].includes(slug.trim()) ) {
    return {
      notFound: true
    };
  }
  let info = await getCandidateProfile(slug);
  if ( !info?.candidacy_set?.length ) {
    return {
      notFound: true
    };
  }
  let callApi, candidacies, contestName, url;

  const contest = info.candidacy_set[0].contest;
  const candidacyId = info.candidacy_set[0].id;

  let [promises, gaffes, stances, quotes, questions] = await Promise.all([
    getCandidateParticulars(candidacyId, "promises"),
    getCandidateParticulars(candidacyId, "gaffes"),
    getCandidateParticulars(candidacyId, "issue_positions"),
    getCandidateParticulars(candidacyId, "testimonials"),
    getCandidateParticulars(candidacyId, "top_questions"),
  ]);

  let particulars = {
    promises,
    gaffes,
    stances,
    quotes,
    questions,
  };

  if (contest.scope === "NATIONAL") {
    switch (contest.display_name.toLowerCase()) {
      case "president":
        contestName = contest.name;
        callApi = getPresidentList;
        url = `${basePath}elections/2022/races/president-vice-president`;
        break;
      case "vice president":
        contestName = contest.name;
        callApi = getVicePresidentList;
        url = `${basePath}elections/2022/races/president-vice-president`;
        break;
      case "senator":
        contestName = contest.name;
        callApi = getSenatorList;
        url = `${basePath}elections/2022/races/senator`;
        break;
    }
  } else if (contest.scope === "LOCAL") {
    contestName = contest.name;
    callApi = () => getLocalCandidatesList(contest.id);
    if (contest.location) {
      try {
        const slug = contest.location.split("/").slice(-2, -1)[0];
        url = `${basePath}elections/2022/${slug}`;
      } catch (err) {
        url = `${basePath}elections/2022/metro-manila`;
      }
    } else {
      url = `${basePath}elections/2022/metro-manila`;
    }
  } else {
    contestName = "";
    candidacies = null;
  }
  if (callApi) {
    try {
      candidacies = await callApi();
    } catch (err) {
      candidacies = null;
    }
  }

  if (!info || !candidacies || !contestName) {
    return {
      props: { candidacies, info, particulars, contestName, url },
      revalidate: 5
    };
  }

  return {
    props: { candidacies, info, particulars, contestName, url },
  };
};

const CandidateInformation = ({
  candidacies,
  info,
  particulars,
  contestName,
  url,
}) => {
  let metaName = `${info.first_name} ${info.last_name}`;
  // if alternate_name is available, add in between the first and last names
  if (info.alternate_name !== null && info.alternate_name !== "") {
    metaName = `${info.first_name} "${info.alternate_name}" ${info.last_name}`;
  }
  // if suffix is available, append to the full name
  if (info.suffix !== null && info.suffix !== "") {
    metaName = `${metaName} ${info.suffix}`;
  }

  const title = `PROFILE: ${metaName} | Candidate for ${info.candidacy_set[0].contest.display_name} - 2022 elections`;
  const description = `${metaName} : Facts, news, top questions, promises and career highlights`;
  const keywords = `2022 Philippine elections, #PHVote, Philippine elections, Philippine national elections, ${metaName}`;
  const og_image = info.header;

  return (
    <>
      <SeoTag
        title={title}
        description={description}
        keywords={keywords}
        og_image={og_image}
        twitter_og_image={og_image}
        twitter_title={title}
        twitter_description={description}
      />
      <CxenseParseTag title={title} />
      <CandidatePage
        contestName={contestName}
        data={info}
        particulars={particulars}
        url={url}
        candidates={candidacies}
      />
    </>
  );
};

export default CandidateInformation;
